<template>
  <div class="edit-live-manage-tracker-geofence-tab box">
    <h2 class="title is-4">Configuration des geofences</h2>

    <b-message type="is-warning" has-icon>
      Cette fonctionnalité est réservée <strong>aux balises Queclink</strong>.
      <br />
      Les changements apportés au cours de l'étape
      <strong>ne seront pas appliqués immédiatement</strong>.
      <br />
      La mise à jour prendra effet une fois que les balises auront récupéré
      <strong>automatiquement</strong> leur configuration (voir estimation ci-dessus). Il est
      possible de <strong>forcer la mise à jour</strong> en resynchronisant manuellement les
      balises.
    </b-message>

    <geofence-map
      :tmp-geofence.sync="tmpGeofence"
      :selected-geofence.sync="selectedGeofence"
      :stage-routes="stageRoutes"
      :stage-waypoints="stageWaypoints"
      :tracker-geofences="filteredGeofences"
      :enable-creation="canAddGeofence"
      @select="selectGeofence"
      @update="updateGeofence"
    />

    <b-message :type="information.type">{{ information.message }}</b-message>

    <b-field label="Format de coordonnées" message="Utile pour les formulaires d'édition/création.">
      <b-select v-model="coordinateFormat" expanded icon="map-marker-alt">
        <option v-for="format in coordinateFormats" :key="format.value" :value="format.value">
          {{ format.name }}{{ format.example }}
        </option>
      </b-select>
    </b-field>

    <hr />

    <div class="columns is-multiline">
      <div class="column is-full">
        <add-form
          v-if="tmpGeofence"
          :live-stage-id="liveStageId"
          :radius.sync="tmpGeofence.radius"
          :location.sync="tmpGeofence.location"
          :coordinate-format="coordinateFormat"
          :live-categories="liveCategories"
          @create="createGeofence"
          @cancel="tmpGeofence = null"
        />
      </div>

      <list-item
        v-for="geofence in trackerGeofences"
        :key="geofence.id"
        :is-open="selectedGeofence?.id === geofence.id"
        :geofence="geofence"
        :disabled="tmpGeofence !== null"
        class="column is-full"
        @open="selectGeofence"
        @cancel="selectedGeofence = null"
        @delete="deleteGeofence"
      >
        <edit-form
          v-if="selectedGeofence?.id === geofence.id"
          :geofence.sync="selectedGeofence"
          :coordinate-format="coordinateFormat"
          :live-categories="liveCategories"
          @update="updateGeofence"
          @cancel="selectedGeofence = null"
        />
      </list-item>
    </div>
  </div>
</template>

<script>
import { STAGE_ACCESS_TYPE_PRIVATE, STAGE_ACCESS_TYPE_PUBLIC } from '@constants/stage/accessType'
import { COORDINATE_FORMATS } from '@constants/settings/coordinate'
import { FORMAT_DD } from '@filters/coordinate'
import ListItem from './ListItem.vue'
import EditForm from './EditForm.vue'
import AddForm from './AddForm.vue'
import GeofenceMap from './Map.vue'

const MAX_GEOGENCES = 10

export default {
  name: 'EditLiveManageTrackerGeofenceTab',

  components: {
    GeofenceMap,
    ListItem,
    AddForm,
    EditForm,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
    liveStageId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      trackerGeofences: [],
      stageWaypoints: [],
      stageRoutes: { public: [], private: [] },
      liveCategories: [],
      tmpGeofence: null,
      selectedGeofence: null,
      coordinateFormat: FORMAT_DD,
    }
  },

  async created() {
    this.coordinateFormats = COORDINATE_FORMATS

    await this.load()
  },

  computed: {
    routes() {
      return this.stageRoutes.private.length > 0
        ? this.stageRoutes.private
        : this.stageRoutes.public.length > 0
        ? this.stageRoutes.public
        : []
    },

    filteredGeofences() {
      // Filter selectedGeofence to preview temporary changes
      if (this.selectedGeofence) {
        return this.trackerGeofences.filter((geofence) => geofence.id !== this.selectedGeofence.id)
      }

      return this.trackerGeofences
    },

    canAddGeofence() {
      return MAX_GEOGENCES - this.trackerGeofences.length >= 1
    },

    information() {
      if (this.tmpGeofence) {
        return {
          type: 'is-info',
          message:
            "Finaliser ou annuler la création du geofence en cours pour intéragir avec d'autres geofences.",
        }
      }

      if (!this.canAddGeofence) {
        return {
          type: 'is-warning',
          message: `Limite maximale de geofences atteinte (${this.trackerGeofences.length} / ${MAX_GEOGENCES})`,
        }
      }

      return {
        type: 'is-info',
        message: `Quota de géofences : ${this.trackerGeofences.length} / ${MAX_GEOGENCES}`,
      }
    },
  },

  methods: {
    createGeofence(geofence) {
      this.trackerGeofences.push(geofence)
      this.tmpGeofence = null
    },

    updateGeofence(geofence) {
      this.trackerGeofences = this.trackerGeofences.map((geo) =>
        geo.id === geofence.id ? geofence : geo,
      )
      this.selectedGeofence = null
    },

    deleteGeofence(geofence) {
      this.trackerGeofences = this.trackerGeofences.filter((geo) => geo.id !== geofence.id)
      this.selectedGeofence = null
    },

    selectGeofence(geofence) {
      if (this.tmpGeofence !== null) {
        return
      }

      // Shallow copy to prevent direct mutation and allow discarding changes.
      this.selectedGeofence = { ...geofence }
    },

    async load() {
      this.loading = true

      await Promise.all([
        this.fetchTrackerGeofences(),
        this.fetchWaypoints(),
        this.fetchStageByAccessType(STAGE_ACCESS_TYPE_PUBLIC),
        this.fetchStageByAccessType(STAGE_ACCESS_TYPE_PRIVATE),
        this.fetchLiveCategories(),
      ])

      this.loading = false
    },

    async fetchTrackerGeofences() {
      try {
        this.trackerGeofences = await this.$services.liveTrackerGeofenceService.getAll(
          this.liveStageId,
        )
      } catch (e) {
        this.trackerGeofences = []
      }
    },

    async fetchStageByAccessType(accessType) {
      try {
        this.stageRoutes[accessType] =
          await this.$services.liveStageRouteService.getAllByAccessType(
            this.liveStageId,
            accessType,
          )
      } catch (e) {
        this.stageRoutes[accessType] = []
      }
    },

    async fetchWaypoints() {
      try {
        this.stageWaypoints = await this.$services.liveStageWaypointService.getAll(this.liveStageId)
      } catch (e) {
        this.stageWaypoints = []
      }
    },

    async fetchLiveCategories() {
      try {
        this.liveCategories = await this.$services.liveCategoryService.getAll(this.id)
      } catch (e) {
        this.liveCategories = []
      }
    },
  },
}
</script>
